import type {
  BrandId,
  CrmStatus,
  CrmSkipReason,
  EventSkipReason,
  NotificationChannel,
  NotificationStatus,
} from '@shared/types';

/** Názvy značek pro filtry a selecty — API vrací totéž v BrandDto.name, tohle je fallback bez čekání na fetch. */
export const BRAND_LABELS: Record<BrandId, string> = {
  buydubai: 'BuyDubai',
  willroi: 'Willroi',
};

/** Všechny časy z API jsou unix timestamp v sekundách. */
export function formatDateTime(ts: number | null | undefined): string {
  if (!ts) return '—';
  return new Date(ts * 1000).toLocaleString('cs-CZ', {
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatDate(ts: number | null | undefined): string {
  if (!ts) return '—';
  return new Date(ts * 1000).toLocaleDateString('cs-CZ', {
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
  });
}

export function formatTime(ts: number | null | undefined): string {
  if (!ts) return '—';
  return new Date(ts * 1000).toLocaleTimeString('cs-CZ', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Relativní čas pro seznam leadů („před 5 min"). Starší než týden → datum. */
export function formatRelative(ts: number | null | undefined): string {
  if (!ts) return '—';
  const diff = Math.floor(Date.now() / 1000) - ts;
  if (diff < 60) return 'právě teď';
  if (diff < 3600) return `před ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `před ${Math.floor(diff / 3600)} h`;
  if (diff < 7 * 86400) return `před ${Math.floor(diff / 86400)} dny`;
  return formatDate(ts);
}

// --- Stav doručení do CRM -------------------------------------------------

export const STATUS_LABELS: Record<CrmStatus, string> = {
  pending: 'Čeká',
  ok: 'Doručeno',
  error: 'Chyba',
  failed: 'Selhalo',
  skipped: 'Přeskočeno',
};

/** Doplňuje `.badge` — barva podle závažnosti (error = jde retryovat, failed = konec). */
export const STATUS_CLASSES: Record<CrmStatus, string> = {
  pending: 'bg-gray-100 text-gray-600',
  ok: 'bg-positive-bg text-positive',
  error: 'bg-warning-bg text-warning',
  failed: 'bg-danger/10 text-danger',
  skipped: 'bg-gray-100 text-gray-400',
};

export const SKIP_REASON_LABELS: Record<CrmSkipReason, string> = {
  'no-phone': 'Chybí telefon',
  unmapped: 'Formulář bez značky',
  import: 'Import — neodesláno',
  disabled: 'Odesílání vypnuté',
};

// --- Doručení do aplikace pro eventy -------------------------------------
// Stavy jsou stejné jako u CRM (STATUS_LABELS / STATUS_CLASSES), liší se jen důvody přeskočení.

export const EVENT_SKIP_REASON_LABELS: Record<EventSkipReason, string> = {
  'no-email': 'Chybí e-mail',
  unconfigured: 'Chybí endpoint nebo token akce',
  import: 'Import — neodesláno',
  disabled: 'Odesílání vypnuté',
  'no-route': 'Odpověď nesedí na žádnou akci',
};

// --- Notifikace (e-mail, Whatchimp) --------------------------------------

export const NOTIFICATION_STATUS_LABELS: Record<NotificationStatus, string> = {
  ok: 'Odesláno',
  error: 'Chyba',
  skipped: 'Přeskočeno',
};

export const NOTIFICATION_STATUS_CLASSES: Record<NotificationStatus, string> = {
  ok: 'bg-positive-bg text-positive',
  error: 'bg-danger/10 text-danger',
  skipped: 'bg-gray-100 text-gray-400',
};

export const CHANNEL_LABELS: Record<NotificationChannel, string> = {
  email_lead: 'E-mail leadovi',
  email_broker: 'E-mail makléři',
  whatchimp_broker: 'WhatsApp makléři',
  whatchimp_lead: 'WhatsApp leadovi',
  email_forward: 'Přeposláno e-mailem',
  email_custom: 'Vlastní e-mail leadovi',
};
